import {Injectable} from '@angular/core';
import {Contract, RoleModel, User} from '../models/user.model';

import * as aes from 'crypto-js/aes';
import * as Utf8 from 'crypto-js/enc-utf8';
import {StorageKeys} from '../shared/enums/storage-keys.enum';
import {BaseService} from "./base.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {BehaviorSubject, catchError, finalize, forkJoin, map, mergeMap, Observable, of, tap} from "rxjs";
import {jwtDecode} from 'jwt-decode';
import {Router} from "@angular/router";
import {RoutesEnum} from "../shared/enums/routes.enum";
import {LoginService} from './login.service';
import {environment} from "../../environments/environment";
import {UserInfoRequest} from "../models/user-info-request.model";
import {UserInfoResponse} from "../models/user-info-response.model";
import {LoaderService} from "./loader.service";
import {ThemeService} from "./theme.service";
import {Screen} from "../shared/enums/role.enum";
import {UserTypeEnum} from '../shared/enums/user-type.enum';

@Injectable({providedIn: 'root'})
export class UserService extends BaseService {
  private readonly baseUrl: string = environment.baseApi;
  private readonly _contractSelected$: BehaviorSubject<Contract | undefined> = new BehaviorSubject<Contract | undefined>(undefined);
  usuariosDaSessao$: BehaviorSubject<Map<any, any>> = new BehaviorSubject<any>(null);
  private static readonly KEY: string = "hyhheyh5ytdFJYFG==";
  loginToastMessage: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    public http: HttpClient,
    private readonly router: Router,
    private readonly loader: LoaderService,
    private readonly loginService: LoginService,
    private readonly themeService: ThemeService,
  ) {
    super(http);
  }

  get encriptedUser(): any {
    let currentUser = structuredClone(this.decrypt(StorageKeys.USER, true)) ?? {};
    currentUser.contracts = this.getCurrentContractAsList();
    currentUser.permissions = currentUser.permissions?.filter((p: any) => p.contractId === currentUser.contracts[0].id);
    return aes.encrypt(JSON.stringify(currentUser), UserService.KEY).toString();
  }

  login(email: string, password: string): Observable<User> {
    this.loader.putMsgLoader('Realizando login...');
    this.loginService.updateTransporter({
      email,
      password
    })
    const url: string = this.getEndpoint('AUTH_LOGIN');
    return this.postData(url, {
      ...this.loginService.getValues(),
      email,
      password
    }).pipe(
      mergeMap((response: User) => {
        const user = response;
        if (!user || !user.contracts || user.contracts.length === 0)
          throw new Error('Usuário não possui contrato associado');
        user.groups = user.contracts;

        return this.getPermissions(user).pipe(
          mergeMap((enrichData: any[]) =>
            this.getLegacyId(user).pipe(
              map((legacyId) => {
                const finalUser = ({
                  ...user,
                  permissions: enrichData,
                  legacyUserId: legacyId
                } as User);

                this.setTenantCookie();
                this.saveUser(finalUser);
                this.setAuthToken(finalUser.accessToken);
                localStorage.setItem('group', JSON.stringify(finalUser.contracts && finalUser.contracts?.length > 0 ? finalUser.contracts[0] : null));
                return finalUser;
              })
            )
          ),
          finalize(() => this.themeService.reloadNexus())
        );
      })
    );
  }

  private getLegacyId(user: User): Observable<any> {
    this.loader.putMsgLoader('Consultando processamento...');
    const headers = new HttpHeaders({
      'Authorization': user.accessToken
    });
    return this.http.get<any>(`${environment.baseApi}auth/acl/legacyId?userId=${user.id}`, {headers});
  }

  private getPermissions(user: User): Observable<any> {
    this.loader.putMsgLoader('Consultando permissões...');
    let requests: Observable<any>[] = [];
    user.contracts?.forEach(contract => {
      const url = `${this.baseUrl}auth/acl/modules?userId=${user.id}&contractId=${contract.id}`;
      const headers = new HttpHeaders({
        'Authorization': user.accessToken
      });

      requests.push(this.http.get(url, {headers}));
    })
    return forkJoin(requests).pipe(
      map(response => {
        let res: any[] = [];
        response.forEach((item: any) => {
          res.push(...item);
        });
        return res;
      })
    );
  }

  hasPermission(requiredPermission: any[]): boolean {
    const user = this.user;
    if (!user?.permissions) {
      return false;
    }
    const currentContract = this.getCurrentContract();
    const permissionContract: any = user.permissions?.find(
      (p: any) => p.contractId === currentContract?.id
    );
    const allowedPaths = requiredPermission.map(p => p.path);

    return permissionContract?.profiles?.some((profile: any) =>
      profile.profileModuleScreens?.some(
        (pms: any) => allowedPaths.includes(pms.screen?.path)
      )
    );
  }

  canActivateByUserType(allowedUserTypes: string[]): boolean {
    if (!allowedUserTypes || allowedUserTypes.length === 0) return false;
    const currentUserType = this.user?.userType;
    if (!currentUserType) return false;
    return allowedUserTypes.includes(currentUserType);
  }

  getUsers(pageNumber: number, pageSize: number): Observable<any> {
    let url: string = this.buildUrlQueryParams(
      {
        pageNumber: pageNumber,
        pageSize: pageSize,
      },
      this.getEndpoint('USERS')
    );
    return this.getData(url);
  }

  updateSession(userInfo: UserInfoRequest): Observable<UserInfoResponse> {
    return this.http.post(`${environment.baseApi}auth/info/updateSession`, userInfo)
  }

  getActiveSessionByContract(contractId: number): Observable<UserInfoResponse[]> {
    return this.http.get<Array<UserInfoResponse>>(`${environment.baseApi}auth/info/getActiveSessionByContract?contract=${contractId}`)
      .pipe(catchError((_) => of([])))
  }

  // consultaUserLegacyIdPorUserId(userId: number): Observable<any> {
  //   return this.http.get<any>(`${environment.baseApi}users/getLegacyId?userId=${userId}`)
  // }

  get user(): User {
    return this.decrypt(StorageKeys.USER, true) ?? {};
  }

  get userEncrypted(): any {
    return localStorage.getItem(StorageKeys.USER) ?? '';
  }

  saveUser(user: any) {
    this.encrypt(StorageKeys.USER, user, true);
  }

  efetuarLogout(): void {
    localStorage.removeItem(StorageKeys.USER);
    localStorage.removeItem(StorageKeys.TOKEN);
    localStorage.removeItem(StorageKeys.USER_EMAIL);

    this.router.navigate([RoutesEnum.LOGIN]).then(() => {
    });
  }

  redirectToHome() {
    this.router.navigate([RoutesEnum.DASHBOARD_SERVICOS]).then(() => {
    });
  }

  redirectToSelectApp() {
    this.router.navigate([RoutesEnum.DASHBOARD_SERVICOS]).then(() => {
    });
  }

  redirectToChangePassword() {
    this.router.navigate([RoutesEnum.CHANGE_PASSWORD]).then(() => {
    });
  }

  setAuthToken(token: string): void {
    localStorage.setItem(StorageKeys.TOKEN, token);
  }

  getToken() {
    return localStorage.getItem(StorageKeys.TOKEN);
  }

  getRolesFromUser(): RoleModel[] {
    return (this.decrypt(StorageKeys.USER, true) as User)?.roles ?? [];
  }

  updateAuditor(auditorId: string): Observable<any> {
    const url: string = this.getEndpoint('UPDATE_AUDITOR_RECORD');
    return this.putData(url, {
      id: this.user.id,
      auditorRecord: auditorId
    }).pipe(finalize(() => {
      const userDecripted = structuredClone(this.user);
      userDecripted.auditorRecord = Number(auditorId);
      this.saveUser(userDecripted);
    }));
  }

  // hasRoles(roles: Role | Role[]): boolean {
  //   if (!roles)
  //     return false;
  //
  //   if (!Array.isArray(roles))
  //     roles = [roles];
  //   const userRoles = this.getRolesFromUser();
  //   let haveRole = false;
  //
  //
  //   roles.forEach(role => {
  //     const foundRole = userRoles.filter(userRole => userRole.roleRout === role);
  //     if (foundRole.length > 0 && foundRole[0].active) {
  //       haveRole = true;
  //     }
  //   })
  //   return haveRole
  // }

  checkForcePassword() {
    const user = this.decrypt(StorageKeys.USER, true) as User ?? []
    return !!user.forceChangePassword;
  }

  sendToken(auth: string) {
    const url = this.getEndpoint('SEND_TOKEN_LOGIN');
    return this.postData(url, {
      ...this.loginService.getValues(),
      passwordAuth: auth
    }).pipe(
      tap((response) => {
        if (response) {
          // response = this.setRoles(response);
          this.setTenantCookie();
          this.saveUser(response);
          this.setAuthToken(response.accessToken);
          return response;
        }
      }))
  }


  loginFromToken(token: string, email: string) {
    const url: string = this.buildUrlQueryParams({
      email: email,
    }, this.getEndpoint('AUTH_LOGIN_TOKEN'))
    return this.http.get<any>(url, {
      headers: {
        Authorization: token
      }
    }).pipe(map((response) => {
      if (response) {
        // response = this.setRoles(response);
        this.setTenantCookie();
        this.saveUser(response);
        return response;
      }
    }))
  }

  saveFromUserFalcon(user: User) {
    // user = this.setRoles(user);
    this.setTenantCookie();
    this.saveUser(user);
    this.setAuthToken(user.accessToken ?? '');
  }


  forgotPassword(email: string) {
    let url: string = this.getEndpoint('FORGOT_PASS_USER');
    url += `?email=${email}`;
    return this.getData(url);
  }

  register(user: User): Observable<any> {
    const url: string = this.getEndpoint('AUTH_REGISTER');
    return this.postData(url, user);
  }

  update(user: User): Observable<any> {
    const url: string = this.getEndpoint('AUTH_REGISTER');
    return this.putData(url, user);
  }

  delete(userId: number): Observable<any> {
    let url: string = this.getEndpoint('USERS');
    url += `?id=${userId}`
    return this.deleteData(url);
  }

  changePassword(data: any) {
    const url: string = this.getEndpoint('CHANGE_PASSWORD');
    return this.postData(url, data);
  }

  private setTenantCookie(): void {
    const d: Date = new Date();
    d.setTime(d.getTime() + (360 * 24 * 60 * 60 * 1000));
    const expires = `expires=${d.toUTCString()}`;
    document.cookie = `Tenant=f6842ee7-8e0b-4b2e-b93d-d7e542d5c34a; ${expires}`;
  }

  // ENCRYPT
  // -------------------------------

  encrypt(key: string, value: any, shouldParse?: boolean) {
    if (shouldParse) {
      value = JSON.stringify(value);
    }
    const encrypted: any = aes.encrypt(value, UserService.KEY).toString();
    localStorage.setItem(key, encrypted ?? value);
    return encrypted;
  }

  decrypt(key: string, shouldParse?: boolean) {
    try {
      const encrypted: any = localStorage.getItem(key);
      if (encrypted) {
        const bytes: any = aes.decrypt(encrypted, UserService.KEY);
        if (bytes) {
          if (shouldParse) {
            return JSON.parse(bytes.toString(Utf8));
          }
          return bytes.toString(Utf8);
        }
      }
    } catch (erro) {
      console.error(erro);
    }
    return null;
  }

  isLogged(): boolean {
    const jwtToken = this.getToken();
    if (jwtToken) {
      const decodedToken = this.decodeToken(jwtToken);
      return decodedToken?.exp && new Date(decodedToken.exp * 1000).getTime() > new Date().getTime();
    }
    return false;
  }

  setCurrentContract(value: Contract) {
    localStorage.setItem('group', JSON.stringify(value));
    this._contractSelected$.next(value);
  }

  getCurrentContract(): Contract | undefined {
    const contract = localStorage.getItem('group');
    if (contract) {
      return JSON.parse(contract);
    }
    return undefined;
  }

  getCurrentContractAsList(): Contract[] | undefined {
    const contract = localStorage.getItem('group');
    if (contract) {
      return [JSON.parse(contract)];
    }
    return undefined;
  }

  setUserFromToken(token: any) {
    const user: User = this.decrypt(token, true);

    if (!user.contracts || user.contracts.length === 0)
      throw new Error('Usuário não possui contrato associado');

    this.saveUser(user)
    localStorage.setItem('group', JSON.stringify(user.contracts ? user.contracts[0] : null))

  }

  private decodeToken(jwtToken: string): any {
    try {
      return jwtDecode(jwtToken);
    } catch (e) {
      console.error(`erro ao decodificar o token ${jwtToken}`)
      return null;
    }
  }
}
